

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from "react";
import { Guest } from "../types/Guest";
import { GuestType } from "../types/enums/GuestType";
import { useTranslations } from "next-intl";
import {
  getGuests,
  getGuestsByIds,
} from "@/lib/api/handlers/GuestHandling";
import { useMetaCalendar } from "./MetaCalendarProvider";
import { useFields } from "./FieldsProvider";
import { parseFieldsApiRequest } from "@/lib/utils/parser";

type AppointmentGuestContextType = {
  guestState: Guest[];
  setGuestState: React.Dispatch<React.SetStateAction<Guest[]>>;
  getRelevantGuestName: (
    guestId: string,
    noName?: boolean,
  ) => Promise<string>;
  searchGuests: (
    searchTerm: string,
    opts?: { mainGuestOnly?: boolean },
  ) => Promise<Guest[]>;
  isSearching: boolean;
  guestIdsSet: Set<string>;
  setGuestIdsSet: React.Dispatch<React.SetStateAction<Set<string>>>;
  existsGuestName: (guestId: string) => boolean;
  getGuestName: (guest: Guest) => string;
  getRelevantGuest: (guestId: string) => Guest | undefined;
};

const AppointmentGuestContext = createContext<
  AppointmentGuestContextType | undefined
>(undefined);

export const AppointmentGuestProvider = ({
  children,
  eventId,
  guests,
  guestsIds,
  userAdditionalFields: userAdditionalFieldsProps,
}: {
  children: React.ReactNode;
  eventId: string;
  guests: Guest[];
  guestsIds: Set<string>;
  userAdditionalFields?: string[];
}) => {
  const t = useTranslations("slotting");
  const [guestIdsSet, setGuestIdsSet] = useState<Set<string>>(
    new Set<string>(),
  );
  const { allFields } = useFields();

  const metaCalendarContext = useMetaCalendar();

  // Only call the hook if props don't provide userAdditionalFields
  const metaCalendar = !userAdditionalFieldsProps ? metaCalendarContext : null;

  // Use props if available, otherwise use hook value. Default to [] so callers
  // like getRelevantGuestName don't pass undefined into parseFieldsApiRequest
  // (e.g. guestlist mounts this provider without a MetaCalendarProvider).
  const userAdditionalFields =
    userAdditionalFieldsProps || metaCalendar?.userAdditionalFields || [];

  const [guestState, setGuestState] = useState<Guest[]>(guests);
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const searchCacheRef = useRef<Map<string, Guest[]>>(new Map());
  const existingGuests = useRef<Set<string>>(new Set(guestsIds));

  useEffect(() => {
    const reloadAllGuests = async () => {
      try {
        const guests = await getGuestsByIds(
          eventId,
          Array.from(existingGuests.current),
          "",
          [],
          "firstName,lastName,guestId,state," +
            parseFieldsApiRequest(userAdditionalFields!, allFields || []),
          0,
          10000,
        );

        if ("isServerError" in guests) {
          console.error("Error fetching guests:", guests.message);
          return;
        }

        // Update guestState with the fetched guests
        setGuestState(guests);
      } catch (error) {
        console.error("Error fetching guests:", error);
      } finally {
        setGuestIdsSet(new Set<string>());
      }
    };
    // If guestIdsSet is not empty, fetch guests
    if (existingGuests.current.size > 0) {
      reloadAllGuests();
    }
  }, [eventId, userAdditionalFields]);

  // Track changes in guestIdsSet and log them
  useEffect(() => {
    // Skip if guestIdsSet is empty (initial state)
    if (guestIdsSet.size === 0) return;

    const getNewGuests = async () => {
      // Find new guest IDs that aren't in the existing set
      const newGuestIds = [...guestIdsSet].filter(
        (id) => !existingGuests.current.has(id),
      );

      if (newGuestIds.length > 0) {
        try {
          const newGuests = await getGuestsByIds(
            eventId,
            newGuestIds,
            "",
            [],
            "firstName,lastName,guestId,state," +
              parseFieldsApiRequest(userAdditionalFields!, allFields || []),
            0,
            10000,
          );

          if ("isServerError" in newGuests) {
            console.error("Error fetching new guests:", newGuests.message);
            return;
          } else {
            // Update guestState with the new guests
            setGuestState((prev) => [...prev, ...newGuests]);

            // Add the new IDs to the existing guests set
            newGuestIds.forEach((id) => {
              existingGuests.current.add(id);
            });
          }
        } finally {
        }
      }

      setGuestIdsSet(new Set<string>());
    };

    getNewGuests();
  }, [guestIdsSet]);

  const getRelevantGuestName = useCallback(async (
    guestId: string,
    noName?: boolean,
  ): Promise<string> => {
    let guest = guestState.find((i) => i.guestId === guestId);

    if (!guest) {
      const response = await getGuestsByIds(
        eventId,
        [guestId],
        "",
        [],
        "firstName,lastName,guestId,state," +
          parseFieldsApiRequest(userAdditionalFields!, allFields || []),
        0,
        1,
      );

      if (response && typeof response === "object" && "isServerError" in response) {
        return noName ? "" : t("blocker");
      }

      guest = (response as Guest[])[0];
      // Update guestState with the fetched guest
      if (guest) setGuestState((prev) => [...prev, guest!]);
    }

    if (guest?.firstName && guest?.lastName) {
      return `${guest.firstName} ${guest.lastName}`;
    } else if (guest?.firstName) {
      return guest.firstName;
    } else if (guest?.lastName) {
      return guest.lastName;
    }
    if (!noName) return t("blocker");
    return "";
  }, [guestState, eventId, userAdditionalFields, allFields, t]);

  const getGuestName = useCallback(
    (guest: Guest): string => {
      if (guest.firstName && guest.lastName) {
        return `${guest.firstName} ${guest.lastName}`;
      } else if (guest.firstName) {
        return guest.firstName;
      } else if (guest.lastName) {
        return guest.lastName;
      }
      return t("noNameSpecified");
    },
    [guestState, t],
  );

  const getRelevantGuest = useCallback(
    (guestId: string): Guest | undefined => {
      return guestState.find((guest) => guest.guestId === guestId);
    },
    [guestState],
  );

  const existsGuestName = useCallback(
    (guestId: string): boolean => {
      return guestState.some(
        (guest) => guest.guestId === guestId && guest.firstName,
      );
    },
    [guestState],
  );

  const performSearch = useCallback(
    async (
      searchTerm: string,
      opts?: { mainGuestOnly?: boolean },
    ): Promise<Guest[]> => {
      if (!searchTerm) return guestState;

      const term = searchTerm.toLowerCase();
      const mainGuestOnly = !!opts?.mainGuestOnly;

      // Cache key includes the filter so different filter sets don't collide
      const cacheKey = `${term}|mg:${mainGuestOnly}`;
      if (searchCacheRef.current.has(cacheKey)) {
        return searchCacheRef.current.get(cacheKey)!;
      }

      try {
        const response = await getGuests(
          eventId,
          term,
          0,
          10,
          mainGuestOnly
            ? [
                {
                  key: "archived",
                  operator: "=",
                  value: "false",
                },
                {
                  key: "guestType",
                  operator: "=",
                  value: GuestType.MAIN_GUEST,
                },
              ]
            : [
                {
                  key: "archived",
                  operator: "=",
                  value: "false",
                },
              ],

          "createdAt",
          "desc",
        );

        if (response && typeof response === "object" && "isServerError" in response) {
          return [];
        }

        const results = response as Guest[];

        // Cache the results
        searchCacheRef.current.set(cacheKey, results);

        return results;
      } catch (error) {
        console.error("Search error:", error);
        return [];
      }
    },
    [eventId, guestState],
  );

  const searchGuests = useCallback(
    (
      searchTerm: string,
      opts?: { mainGuestOnly?: boolean },
    ): Promise<Guest[]> => {
      return new Promise((resolve) => {
        // Clear existing timeout
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }

        // If search term is empty, return immediately
        if (!searchTerm.trim()) {
          setIsSearching(false);
          resolve(guestState);
          return;
        }

        setIsSearching(true);

        // Set new timeout for debouncing (300ms delay)
        searchTimeoutRef.current = setTimeout(async () => {
          try {
            const results = await performSearch(searchTerm.trim(), opts);
            setIsSearching(false);
            resolve(results);
          } catch {
            setIsSearching(false);
            resolve([]);
          }
        }, 300);
      });
    },
    [performSearch, guestState],
  );

  // Cleanup timeout on unmount
  const contextValue = useMemo(() => ({
    searchGuests, guestState, setGuestState, getRelevantGuestName,
    existsGuestName, isSearching, guestIdsSet, setGuestIdsSet,
    getGuestName, getRelevantGuest,
  }), [searchGuests, guestState, getRelevantGuestName, existsGuestName,
    isSearching, guestIdsSet, getGuestName, getRelevantGuest]);

  return (
    <AppointmentGuestContext.Provider value={contextValue}>
      {children}
    </AppointmentGuestContext.Provider>
  );
};

export const useAppointmentGuest = () => {
  const context = useContext(AppointmentGuestContext);
  if (!context) {
    throw new Error(
      "useAppointmentGuest must be used within a AppointmentGuestProvider",
    );
  }
  return context;
};
